import {
  AppstoreOutlined,
  BarChartOutlined,
  BranchesOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  DatabaseOutlined,
  DollarOutlined,
  EllipsisOutlined,
  GiftOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  MailOutlined,
  ReloadOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import type { TableColumnsType } from 'antd'
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import client from '@/api/client'
import { unwrapApiResult } from '@/api/unwrapApiResult'
import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE, MonoId, truncateMiddle } from '@/components/common/MonoId'
import { StatusTag } from '@/components/common/StatusTag'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import {
  ACCOUNT_STATUS_COLORS,
  PUBLIC_IP_STATUSES,
  STORAGE_STATUSES,
  SUBNET_STATUSES,
  VM_STATUSES,
  VPC_STATUSES,
} from '@/constants/statuses'
import type { PaginatedItems } from '@/types/api'
import { toIsoBoundary } from '@/utils/dateRange'

import { ChangeBalanceModal } from './components/ChangeBalanceModal'
import { SetGroupModal } from './components/SetGroupModal'
import { SetRoleModal } from './components/SetRoleModal'
import { UserNotificationsTab } from './components/UserNotificationsTab'
import { useUserActionMenu } from './hooks/useUserActionMenu'
import { useUserActions } from './hooks/useUserActions'

type AdminUserDto = components['schemas']['AdminUserDto']
type AdminUserProfileDto = components['schemas']['AdminUserProfileDto']
type UserVmDto = components['schemas']['AdminUserVmDto']
type UserStorageDto = components['schemas']['UserStorageDto']
type UserPublicIpDto = components['schemas']['UserPublicIpDto']
type UserVpcDto = components['schemas']['UserVpcDto']
type UserSubnetDto = components['schemas']['UserSubnetDto']
type ChargeDto = components['schemas']['ChargeDto']
type EntitlementValueDto = components['schemas']['EntitlementValueDto']
type GrantDto = components['schemas']['GrantDto']
type TopUpDto = components['schemas']['TopUpDto']
type TotalChargesEntry = components['schemas']['TotalChargesEntry']
type TotalChargesResponse = components['schemas']['TotalChargesResponse']

export default function UserShowPage() {
  const { id: userId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'info'
  const { activeModal, closeModal, handleAction, getMenuItems } =
    useUserActionMenu()

  const userQuery = useCustom<AdminUserDto>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}`,
    method: 'get',
  })

  const profileQuery = useCustom<AdminUserProfileDto>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/profile`,
    method: 'get',
  })

  const balanceQuery = useCustom({
    url: `${VODOPAD_ENDPOINT}/v2/admin/users/${userId}/balances`,
    method: 'get',
  })

  const user = userQuery.query.data?.data as AdminUserDto | undefined
  const profile = profileQuery.query.data?.data as
    | AdminUserProfileDto
    | undefined
  const balances = balanceQuery.query.data?.data

  if (!userId) return null

  if (userQuery.query.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/users">Users</Link> },
          { title: user?.email ?? userId },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>{user?.email ?? 'User Details'}</h2>
        {user && (
          <Dropdown
            menu={{
              items: getMenuItems(user),
              onClick: ({ key }) => void handleAction(key, user),
            }}
            trigger={['click']}
          >
            <Button type="primary">
              Actions <EllipsisOutlined />
            </Button>
          </Dropdown>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        items={[
          {
            key: 'info',
            label: 'Overview',
            icon: <InfoCircleOutlined />,
            children: (
              <UserInfoTab
                user={user}
                profile={profile}
                balances={balances}
                userId={userId}
                onRefresh={() => void balanceQuery.query.refetch()}
              />
            ),
          },
          {
            key: 'billing',
            label: 'Billing',
            icon: <DollarOutlined />,
            children: (
              <UserBillingTab userId={userId} userEmail={user?.email} />
            ),
          },
          {
            key: 'resources',
            label: 'Resources',
            icon: <AppstoreOutlined />,
            children: <UserResourcesTab userId={userId} />,
          },
          {
            key: 'notifications',
            label: 'Notifications',
            icon: <MailOutlined />,
            children: <UserNotificationsTab userId={userId} />,
          },
        ]}
      />

      <ChangeBalanceModal
        open={activeModal === 'balance'}
        userId={userId}
        userEmail={user?.email}
        onClose={() => {
          closeModal()
          void balanceQuery.query.refetch()
        }}
      />
      <SetRoleModal
        open={activeModal === 'role'}
        userIds={[userId]}
        userEmail={user?.email}
        currentRole={user?.roleName}
        onClose={() => {
          closeModal()
          void userQuery.query.refetch()
        }}
      />
      <SetGroupModal
        open={activeModal === 'group'}
        userIds={[userId]}
        userEmail={user?.email}
        currentGroup={user?.groupName}
        onClose={() => {
          closeModal()
          void userQuery.query.refetch()
        }}
      />
    </div>
  )
}

function UserInfoTab({
  user,
  profile,
  balances,
  userId,
  onRefresh,
}: {
  user: AdminUserDto | undefined
  profile: AdminUserProfileDto | undefined
  balances: unknown
  userId: string
  onRefresh: () => void
}) {
  const [refreshing, setRefreshing] = useState(false)

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const result = await client.POST(
        '/v1/admin/users/{user_id}/balances/refresh',
        { params: { path: { user_id: userId } } },
      )
      unwrapApiResult(result, 'Failed to refresh balance')
      void message.success('Balance refreshed')
      onRefresh()
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setRefreshing(false)
    }
  }

  if (!user) return null

  let balanceList: EntitlementValueDto[] = []
  if (balances) {
    balanceList = Array.isArray(balances)
      ? balances
      : [balances as EntitlementValueDto]
  }

  return (
    <div>
      <Card title="User Information" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="ID">
            <Typography.Text copyable style={MONO_STYLE}>
              {user.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <Typography.Text copyable>{user.email}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            <StatusTag
              status={user.roleName}
              colorMap={{ SuperAdmin: 'red', Admin: 'red', Customer: 'blue' }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Group">
            {user.groupName ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <StatusTag
              status={user.accountStatus}
              colorMap={ACCOUNT_STATUS_COLORS}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Payment Type">
            {user.paymentType}
          </Descriptions.Item>
          {profile && (
            <>
              <Descriptions.Item label="First Name">
                {profile.firstName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Last Name">
                {profile.lastName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {profile.company || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Job Role">
                {profile.jobRole || '—'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Registered">
            <FormattedDate date={user.registeredAt} fmt="PPp" />
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            <FormattedDate date={user.lastLoginAt} fmt="PPp" />
          </Descriptions.Item>
          <Descriptions.Item label="Synced to OpenMeter">
            <FormattedDate date={user.syncedToOpenmeterAt} fmt="PPp" />
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <div style={{ marginBottom: 16 }}>
        <Popconfirm
          title={`Refresh balance for ${user.email}?`}
          okText="Refresh Balance"
          onConfirm={handleRefresh}
        >
          <Button icon={<ReloadOutlined />} loading={refreshing}>
            Refresh Balance
          </Button>
        </Popconfirm>
      </div>
      {balanceList.length > 0 && (
        <Row gutter={[16, 16]}>
          {balanceList.map((b, i) => (
            <Col key={i} xs={24}>
              <BalanceStatsCard balance={b} />
            </Col>
          ))}
        </Row>
      )}
    </div>
  )
}

function BalanceStatsCard({ balance: b }: { balance: EntitlementValueDto }) {
  return (
    <Card title="Stats">
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} lg={4}>
          <Statistic
            title="Balance"
            value={Number(b.balance).toFixed(2)}
            prefix="$"
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Statistic
            title="Usage"
            value={Number(b.usage).toFixed(2)}
            prefix="$"
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Statistic
            title="Estimated Usage"
            value={Number(b.estimatedUsage).toFixed(2)}
            prefix="$"
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Statistic
            title="Overage"
            value={Number(b.overage).toFixed(2)}
            prefix="$"
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Statistic
            title="Total Deposited"
            value={Number(b.totalDepositedAmount).toFixed(2)}
            prefix="$"
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Statistic
            title="Days Left"
            value={b.usageDaysLeft ?? 'N/A'}
            suffix={b.usageDaysLeft != null ? 'days' : undefined}
          />
        </Col>
      </Row>
    </Card>
  )
}

const CHARGE_RESOURCE_TYPES = [
  'VM',
  'PUBLIC_IP',
  'STORAGE',
  'SUBNET',
  'VPC',
  'WORKSPACE',
] as const

const AGGREGATED_COLUMNS: TableColumnsType<TotalChargesEntry> = [
  {
    title: 'Resource Name',
    dataIndex: 'resourceName',
    key: 'resourceName',
    render: (name: string | null, r: TotalChargesEntry) => {
      if (!name) return '—'
      const path =
        r.resourceType && r.resourceId
          ? resourcePath(r.resourceType, r.resourceId)
          : null
      return path ? <Link to={path}>{name}</Link> : name
    },
  },
  { title: 'Resource Type', dataIndex: 'resourceType', key: 'resourceType' },
  {
    title: 'Total Cost',
    dataIndex: 'totalCost',
    key: 'totalCost',
    render: (v: string) => `$${Number(v).toFixed(2)}`,
  },
  {
    title: 'Usage Hours',
    dataIndex: 'totalUsageHours',
    key: 'totalUsageHours',
    render: (v: string) => `${Number(v).toFixed(1)} h`,
  },
  { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
]

function resourcePath(type: string, id: string): string | null {
  switch (type) {
    case 'VM':
      return `/compute/${id}`
    case 'STORAGE':
      return `/storage/${id}`
    case 'PUBLIC_IP':
      return `/network/${id}`
    case 'SUBNET':
      return `/subnets/${id}`
    case 'VPC':
      return `/vpcs/${id}`
    default:
      return null
  }
}

function UserBillingTab({
  userId,
  userEmail,
}: {
  userId: string
  userEmail?: string
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const billingTab = searchParams.get('billingTab') ?? 'usage'

  return (
    <Tabs
      activeKey={billingTab}
      onChange={(key) =>
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set('billingTab', key)
            return next
          },
          { replace: true },
        )
      }
      items={[
        {
          key: 'usage',
          label: 'Usage',
          icon: <BarChartOutlined />,
          children: <UsageTab userId={userId} />,
        },
        {
          key: 'deposits',
          label: 'Deposits',
          icon: <WalletOutlined />,
          children: <DepositsTab userId={userId} />,
        },
        {
          key: 'charges',
          label: 'Charges',
          icon: <DollarOutlined />,
          children: <ChargesTab userId={userId} />,
        },
        {
          key: 'grants',
          label: 'Grants',
          icon: <GiftOutlined />,
          children: <GrantsTab userId={userId} userEmail={userEmail} />,
        },
      ]}
    />
  )
}

function UsageTab({ userId }: { userId: string }) {
  const [summaryDates, setSummaryDates] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ])

  const totalUrl = `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/billing/charges/total`

  const selectionStart = summaryDates?.[0]?.format('YYYY-MM-DD') ?? ''
  const selectionEnd = summaryDates?.[1]?.format('YYYY-MM-DD') ?? ''

  const yearStart = dayjs().startOf('year').format('YYYY-MM-DD')
  const yearEnd = dayjs().endOf('year').format('YYYY-MM-DD')
  const monthStart = dayjs().startOf('month').format('YYYY-MM-DD')
  const monthEnd = dayjs().endOf('month').format('YYYY-MM-DD')
  const today = dayjs().format('YYYY-MM-DD')

  // Key all four queries on their [startDate, endDate] pair so identical
  // windows (e.g. the default "This month" selection vs the fixed month card)
  // share one cache entry instead of making duplicate requests.
  const hasSelection = summaryDates !== null
  const totalQuery = useCustom<TotalChargesResponse>({
    url: totalUrl,
    method: 'get',
    config: {
      query: {
        ...(selectionStart ? { startDate: selectionStart } : {}),
        ...(selectionEnd ? { endDate: selectionEnd } : {}),
      },
    },
    queryOptions: {
      queryKey: [totalUrl, selectionStart, selectionEnd],
      enabled: hasSelection,
    },
  })

  const yearQuery = useCustom<TotalChargesResponse>({
    url: totalUrl,
    method: 'get',
    config: { query: { startDate: yearStart, endDate: yearEnd } },
    queryOptions: { queryKey: [totalUrl, yearStart, yearEnd] },
  })

  const monthQuery = useCustom<TotalChargesResponse>({
    url: totalUrl,
    method: 'get',
    config: { query: { startDate: monthStart, endDate: monthEnd } },
    queryOptions: { queryKey: [totalUrl, monthStart, monthEnd] },
  })

  const todayQuery = useCustom<TotalChargesResponse>({
    url: totalUrl,
    method: 'get',
    config: { query: { startDate: today, endDate: today } },
    queryOptions: { queryKey: [totalUrl, today, today] },
  })

  const aggregatedQuery = useCustom<TotalChargesEntry[]>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/billing/charges/aggregated`,
    method: 'get',
    config: {
      query: {
        startDate: summaryDates?.[0]?.format('YYYY-MM-DD') ?? '',
        endDate: summaryDates?.[1]?.format('YYYY-MM-DD') ?? '',
      },
    },
    queryOptions: { enabled: summaryDates !== null },
  })

  const totalData = totalQuery.query.data?.data as
    | TotalChargesResponse
    | undefined
  const yearData = yearQuery.query.data?.data as
    | TotalChargesResponse
    | undefined
  const monthData = monthQuery.query.data?.data as
    | TotalChargesResponse
    | undefined
  const todayData = todayQuery.query.data?.data as
    | TotalChargesResponse
    | undefined
  const aggregated =
    (aggregatedQuery.query.data?.data as TotalChargesEntry[] | undefined) ?? []

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {(
          [
            {
              title: 'Total (selection)',
              data: hasSelection ? totalData : undefined,
              loading: hasSelection && totalQuery.query.isLoading,
            },
            {
              title: 'This year',
              data: yearData,
              loading: yearQuery.query.isLoading,
            },
            {
              title: 'This month',
              data: monthData,
              loading: monthQuery.query.isLoading,
            },
            {
              title: 'Today',
              data: todayData,
              loading: todayQuery.query.isLoading,
            },
          ] as const
        ).map(({ title, data, loading }) => (
          <Col key={title} xs={12} sm={6} lg={4}>
            <Card>
              <Statistic
                title={title}
                value={data ? Number(data.total).toFixed(2) : '—'}
                prefix={data ? '$' : undefined}
                loading={loading}
              />
            </Card>
          </Col>
        ))}
      </Row>
      <div style={{ marginBottom: 16 }}>
        <DatePicker.RangePicker
          value={summaryDates}
          onChange={(d) => setSummaryDates(d as [Dayjs, Dayjs] | null)}
          allowClear
        />
      </div>
      {summaryDates ? (
        <Table
          loading={aggregatedQuery.query.isLoading}
          dataSource={aggregated}
          rowKey={(r) =>
            [r.resourceId, r.resourceType, r.periodStart, r.ipAddressType]
              .filter(Boolean)
              .join('|') || 'total'
          }
          columns={AGGREGATED_COLUMNS}
          pagination={{ pageSize: 10 }}
        />
      ) : (
        <Typography.Text type="secondary">
          Select a date range to see the usage breakdown.
        </Typography.Text>
      )}
    </div>
  )
}

function DepositsTab({ userId }: { userId: string }) {
  const [depositDates, setDepositDates] = useState<[Dayjs, Dayjs] | null>(null)

  const depositsQuery = useCustom<PaginatedItems<TopUpDto>>({
    url: `${VODOPAD_ENDPOINT}/v2/admin/users/${userId}/billing/deposits`,
    method: 'get',
    config: {
      query: {
        page: 1,
        perPage: 50,
        ...(depositDates?.[0]
          ? { start: toIsoBoundary(depositDates[0], 'start') }
          : {}),
        ...(depositDates?.[1]
          ? { end: toIsoBoundary(depositDates[1], 'end') }
          : {}),
      },
    },
  })

  const deposits =
    (depositsQuery.query.data?.data as PaginatedItems<TopUpDto> | undefined)
      ?.items ?? []

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <DatePicker.RangePicker
          onChange={(d) => setDepositDates(d as [Dayjs, Dayjs] | null)}
          allowClear
        />
      </div>
      <Table
        loading={depositsQuery.query.isLoading}
        dataSource={deposits}
        rowKey={(_, i) => String(i)}
        columns={[
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (v: number) => `$${(v / 1e8).toFixed(2)}`,
          },
          {
            title: 'Currency',
            dataIndex: 'currency',
            key: 'currency',
            width: 100,
            render: (currency: string) => <Tag>{currency}</Tag>,
          },
          {
            title: 'Fees',
            key: 'fees',
            width: 120,
            render: (_: unknown, record: TopUpDto) =>
              `$${((record.copperxFee + record.copperxGasFee) / 1e8).toFixed(2)}`,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s: string) => <StatusTag status={s} />,
          },
          {
            title: 'CopperX Session',
            dataIndex: 'copperxSessionId',
            key: 'copperxSessionId',
            render: (id: string | null) => <MonoId id={id} />,
          },
          {
            title: 'Tx',
            dataIndex: 'balanceContractTxAddress',
            key: 'balanceContractTxAddress',
            render: (tx: string | null) => <MonoId id={tx} />,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
          {
            title: 'Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
        ]}
        pagination={{ pageSize: 5 }}
      />
    </div>
  )
}

function ChargesTab({ userId }: { userId: string }) {
  const [chargeResourceType, setChargeResourceType] = useState<
    string | undefined
  >()
  const [chargeResourceId, setChargeResourceId] = useState('')
  const [chargeDates, setChargeDates] = useState<[Dayjs, Dayjs] | null>(null)

  const chargesQuery = useCustom<PaginatedItems<ChargeDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/billing/charges`,
    method: 'get',
    config: {
      query: {
        page: 1,
        perPage: 50,
        ...(chargeResourceType ? { resourceType: chargeResourceType } : {}),
        ...(chargeResourceId ? { resourceId: chargeResourceId } : {}),
        ...(chargeDates?.[0]
          ? { startDate: chargeDates[0].format('YYYY-MM-DD') }
          : {}),
        ...(chargeDates?.[1]
          ? { endDate: chargeDates[1].format('YYYY-MM-DD') }
          : {}),
      },
    },
  })

  const charges =
    (chargesQuery.query.data?.data as PaginatedItems<ChargeDto> | undefined)
      ?.items ?? []

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="Resource Type"
          allowClear
          onChange={(v) => setChargeResourceType(v)}
          options={CHARGE_RESOURCE_TYPES.map((t) => ({ label: t, value: t }))}
          style={{ width: 160 }}
        />
        <Input.Search
          placeholder="Resource ID"
          allowClear
          onSearch={(v) => setChargeResourceId(v)}
          style={{ width: 300 }}
        />
        <DatePicker.RangePicker
          onChange={(d) => setChargeDates(d as [Dayjs, Dayjs] | null)}
          allowClear
        />
      </Space>
      <Table
        loading={chargesQuery.query.isLoading}
        dataSource={charges}
        rowKey="id"
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <MonoId id={id} />,
          },
          {
            title: 'Resource Type',
            dataIndex: 'resourceType',
            key: 'resourceType',
          },
          {
            title: 'Resource ID',
            dataIndex: 'resourceId',
            key: 'resourceId',
            render: (id: string, record: ChargeDto) => (
              <MonoId
                id={id}
                to={resourcePath(record.resourceType, id) ?? undefined}
              />
            ),
          },
          {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
            render: (v: string) => `$${Number(v).toFixed(2)}`,
          },
          {
            title: 'Charge At',
            dataIndex: 'chargeAt',
            key: 'chargeAt',
            render: (d: string | null) =>
              d ? <FormattedDate date={d} fmt="PPp" /> : '—',
          },
          {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
        ]}
        pagination={{ pageSize: 5 }}
      />
    </div>
  )
}

function GrantsTab({
  userId,
  userEmail,
}: {
  userId: string
  userEmail?: string
}) {
  const [grantDates, setGrantDates] = useState<[Dayjs, Dayjs] | null>(null)
  const { voidGrant } = useUserActions()
  const [revokingId, setRevokingId] = useState<string | null>(null)

  const handleRevoke = async (grantId: string) => {
    setRevokingId(grantId)
    try {
      await voidGrant(userId, grantId)
    } finally {
      setRevokingId(null)
    }
  }

  const grantsQuery = useCustom<PaginatedItems<GrantDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/balances/grants`,
    method: 'get',
    config: {
      query: {
        page: 1,
        perPage: 50,
        ...(grantDates?.[0]
          ? { start: toIsoBoundary(grantDates[0], 'start') }
          : {}),
        ...(grantDates?.[1]
          ? { end: toIsoBoundary(grantDates[1], 'end') }
          : {}),
      },
    },
  })

  const grants =
    (grantsQuery.query.data?.data as PaginatedItems<GrantDto> | undefined)
      ?.items ?? []

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <DatePicker.RangePicker
          onChange={(d) => setGrantDates(d as [Dayjs, Dayjs] | null)}
          allowClear
        />
      </div>
      <Table
        loading={grantsQuery.query.isLoading}
        dataSource={grants}
        rowKey="id"
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <MonoId id={id} />,
          },
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (v: string) => `$${Number(v).toFixed(2)}`,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (s: string) => <StatusTag status={s} />,
          },
          {
            title: 'Recurrence',
            key: 'recurrence',
            width: 160,
            render: (_: unknown, record: GrantDto) =>
              record.recurrencePeriod ? (
                <Space size={4}>
                  <Tag color="blue">every {record.recurrencePeriod}</Tag>
                  {record.recurrenceAnchor && (
                    <FormattedDate date={record.recurrenceAnchor} />
                  )}
                </Space>
              ) : (
                'one-off'
              ),
          },
          {
            title: 'OpenMeter ID',
            dataIndex: 'openmeterId',
            key: 'openmeterId',
            render: (id: string | null) => <MonoId id={id} />,
          },
          {
            title: 'Sent At',
            dataIndex: 'sentAt',
            key: 'sentAt',
            render: (d: string | null) =>
              d ? <FormattedDate date={d} fmt="PPp" /> : '—',
          },
          {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
          {
            title: 'Voided At',
            dataIndex: 'voidedAt',
            key: 'voidedAt',
            render: (d: string | undefined) =>
              d ? <FormattedDate date={d} fmt="PPp" /> : '—',
          },
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, record: GrantDto) => {
              if (record.status === 'voided' || !record.openmeterId) return null
              return (
                <Popconfirm
                  title={`Revoke $${Number(record.amount).toFixed(2)} grant for ${userEmail ?? userId}?`}
                  description={`Grant ${truncateMiddle(record.id)} — this will void the remaining credits.`}
                  okText="Revoke"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleRevoke(record.id)}
                >
                  <Button
                    size="small"
                    danger
                    loading={revokingId === record.id}
                  >
                    Revoke
                  </Button>
                </Popconfirm>
              )
            },
          },
        ]}
        pagination={{ pageSize: 5 }}
      />
    </div>
  )
}

function UserResourcesTab({ userId }: { userId: string }) {
  return (
    <Tabs
      items={[
        {
          key: 'compute',
          label: 'Compute',
          icon: <CloudServerOutlined />,
          children: <UserComputeTab userId={userId} />,
        },
        {
          key: 'storage',
          label: 'Storage',
          icon: <DatabaseOutlined />,
          children: <UserStorageTab userId={userId} />,
        },
        {
          key: 'network',
          label: 'Network',
          icon: <GlobalOutlined />,
          children: <UserNetworkTab userId={userId} />,
        },
        {
          key: 'vpcs',
          label: 'VPCs',
          icon: <ClusterOutlined />,
          children: <UserVpcsTab userId={userId} />,
        },
        {
          key: 'subnets',
          label: 'Subnets',
          icon: <BranchesOutlined />,
          children: <UserSubnetsTab userId={userId} />,
        },
      ]}
    />
  )
}

function StatusFilter({
  statuses,
  onChange,
}: {
  statuses: readonly string[]
  onChange: (v: string | undefined) => void
}) {
  return (
    <Select
      placeholder="Status"
      style={{ width: 160, marginBottom: 12 }}
      allowClear
      onChange={onChange}
      options={statuses.map((s) => ({ label: s, value: s }))}
    />
  )
}

function UserResourceList<T extends object>({
  userId,
  url,
  statuses,
  columns,
}: {
  userId: string
  url: string
  statuses: readonly string[]
  columns: TableColumnsType<T>
}) {
  const [status, setStatus] = useState<string | undefined>()
  const query = useCustom<PaginatedItems<T>>({
    url,
    method: 'get',
    config: {
      query: { userId, page: 1, perPage: 50, ...(status ? { status } : {}) },
    },
  })
  const items =
    (query.query.data?.data as PaginatedItems<T> | undefined)?.items ?? []
  return (
    <div>
      <StatusFilter statuses={statuses} onChange={setStatus} />
      <Table<T>
        loading={query.query.isLoading}
        dataSource={items}
        rowKey="id"
        pagination={{ pageSize: 5 }}
        columns={columns}
      />
    </div>
  )
}

function UserComputeTab({ userId }: { userId: string }) {
  return (
    <UserResourceList<UserVmDto>
      userId={userId}
      url={`${VODOPAD_ENDPOINT}/v2/admin/vms`}
      statuses={VM_STATUSES}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, r: UserVmDto) => (
            <Link to={`/compute/${r.id}`}>{name}</Link>
          ),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: (s: string) => <StatusTag status={s} />,
        },
        {
          title: 'Cluster',
          dataIndex: 'clusterId',
          key: 'clusterId',
          render: (id: string) => <ClusterNameLink clusterId={id} />,
        },
        {
          title: 'Created',
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
        },
      ]}
    />
  )
}

function UserStorageTab({ userId }: { userId: string }) {
  return (
    <UserResourceList<UserStorageDto>
      userId={userId}
      url={`${VODOPAD_ENDPOINT}/v1/admin/storages`}
      statuses={STORAGE_STATUSES}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, r: UserStorageDto) => (
            <Link to={`/storage/${r.id}`}>{name}</Link>
          ),
        },
        {
          title: 'Type',
          dataIndex: 'storageType',
          key: 'storageType',
          width: 80,
        },
        {
          title: 'Volume (GB)',
          dataIndex: 'volumeGb',
          key: 'volumeGb',
          width: 110,
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: (s: string) => <StatusTag status={s} />,
        },
        {
          title: 'Cluster',
          dataIndex: 'clusterId',
          key: 'clusterId',
          render: (id: string) => <ClusterNameLink clusterId={id} />,
        },
        {
          title: 'Created',
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
        },
      ]}
    />
  )
}

function UserNetworkTab({ userId }: { userId: string }) {
  return (
    <UserResourceList<UserPublicIpDto>
      userId={userId}
      url={`${VODOPAD_ENDPOINT}/v1/admin/public_ips`}
      statuses={PUBLIC_IP_STATUSES}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, r: UserPublicIpDto) => (
            <Link to={`/network/${r.id}`}>{name}</Link>
          ),
        },
        {
          title: 'Address',
          dataIndex: 'address',
          key: 'address',
          render: (a: string | null) =>
            a ? (
              <Typography.Text copyable>{a}</Typography.Text>
            ) : (
              <Tag>Pending</Tag>
            ),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: (s: string) => <StatusTag status={s} />,
        },
        {
          title: 'Created',
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
        },
      ]}
    />
  )
}

function UserVpcsTab({ userId }: { userId: string }) {
  return (
    <UserResourceList<UserVpcDto>
      userId={userId}
      url={`${VODOPAD_ENDPOINT}/v1/admin/vpcs`}
      statuses={VPC_STATUSES}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, r: UserVpcDto) => (
            <Link to={`/vpcs/${r.id}`}>{name}</Link>
          ),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: (s: string) => <StatusTag status={s} />,
        },
        {
          title: 'Cluster',
          dataIndex: 'clusterId',
          key: 'clusterId',
          render: (id: string) => <ClusterNameLink clusterId={id} />,
        },
        {
          title: 'Subnets',
          dataIndex: 'subnetsCount',
          key: 'subnetsCount',
          width: 80,
        },
        {
          title: 'External',
          dataIndex: 'enableExternal',
          key: 'enableExternal',
          width: 80,
          render: (v: boolean | null) => (v ? 'Yes' : 'No'),
        },
        {
          title: 'Created',
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
        },
      ]}
    />
  )
}

function UserSubnetsTab({ userId }: { userId: string }) {
  return (
    <UserResourceList<UserSubnetDto>
      userId={userId}
      url={`${VODOPAD_ENDPOINT}/v1/admin/subnets`}
      statuses={SUBNET_STATUSES}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (name: string, r: UserSubnetDto) => (
            <Link to={`/subnets/${r.id}`}>{name}</Link>
          ),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          render: (s: string) => <StatusTag status={s} />,
        },
        {
          title: 'IPv4 CIDR',
          dataIndex: 'ipv4Cidr',
          key: 'ipv4Cidr',
          render: (v: string | null) =>
            v ? <Typography.Text copyable>{v}</Typography.Text> : '—',
        },
        {
          title: 'IPv6 CIDR',
          dataIndex: 'ipv6Cidr',
          key: 'ipv6Cidr',
          render: (v: string | null) =>
            v ? <Typography.Text copyable>{v}</Typography.Text> : '—',
        },
        {
          title: 'Cluster',
          dataIndex: 'clusterId',
          key: 'clusterId',
          render: (id: string) => <ClusterNameLink clusterId={id} />,
        },
      ]}
    />
  )
}
